import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, selectLoginState, resetLoginState, fetchCartItems } from "../redux/WithcookieSlice";
import { useTranslation } from "../redux/TranslationHelper";

const LoginForm = ({ style = {}, componentProps = {}, ...props }) => {
  // componentProps에서 값 추출
  const backgroundType = componentProps.backgroundType || 'image';
  const backgroundUrl = componentProps.backgroundUrl || '';
  const redirectPath = componentProps.redirectPath || '';

  console.log('[LoginComponent] backgroundType', backgroundType);
  console.log('[LoginComponent] backgroundUrl', backgroundUrl);
  console.log('[LoginComponent] redirectPath', redirectPath);
  console.log('[LoginComponent] style', style);
  console.log('[LoginComponent] ...props', props);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading, loginSuccess, loginError, validationErrors: reduxValidationErrors } = useSelector(selectLoginState);
  const withcookieData = useSelector(state => state.withcookie.withcookieData);
  const isUserLoggedIn = useSelector(state => state.withcookie.isUserLoggedIn);
  
  // 테마 색상 설정
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#007bff";
  const secondaryColor = colorSet?.secondary || "#6c757d";

  // 로컬 상태 관리
  const [formData, setFormData] = useState({
    user_id: "",
    password: ""
  });
  const [localValidationErrors, setLocalValidationErrors] = useState({});

  // 리덕스의 validation 에러와 로컬 에러 병합
  const validationErrors = { ...localValidationErrors, ...reduxValidationErrors };

  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  // 컴포넌트 언마운트 시 로그인 상태 초기화
  useEffect(() => {
    return () => {
      dispatch(resetLoginState());
    };
  }, [dispatch]);

  // 이미 로그인된 경우 리다이렉트
  useEffect(() => {
    if (isUserLoggedIn && redirectPath) {
      window.location.href = redirectPath;
    }
  }, [isUserLoggedIn, redirectPath]);

  // 로그인 성공 시 리다이렉트
  useEffect(() => {
    if (loginSuccess) {
      dispatch(fetchCartItems());
      const timer = setTimeout(() => {
        if (redirectPath) {
          window.location.href = redirectPath;
        } else {
          window.location.href = '/';
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loginSuccess, dispatch, redirectPath]);

  // 컴포넌트 마운트 후 1초 뒤에 임의 액션으로 소리 켜기 시도
  useEffect(() => {
    if (backgroundType === 'video' && backgroundUrl) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.volume = 1.0;
          videoRef.current.play().catch(() => {});
          setIsMuted(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [backgroundType, backgroundUrl]);

  // 소리 켜기 버튼 핸들러
  const handleUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.volume = 1.0;
      videoRef.current.play().catch(() => {});
      setIsMuted(false);
    }
  };

  // 외부 style에서 레이아웃 관련 속성 제거
  const filterLayoutStyle = (styleObj) => {
    if (!styleObj) return {};
    const { width, height, display, alignItems, justifyContent, ...rest } = styleObj;
    return rest;
  };

  const defaultStyle = {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Noto Sans KR, sans-serif',
    maxWidth: 'min(500px, 95vw)', // 여기만 변경
    minWidth: '400px',
    margin: '30px auto',
    height: 'auto',
    ...filterLayoutStyle(style)
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    width: '100%'
  };

  const formRowStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%'
  };

  const inputStyle = {
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    transition: 'border-color 0.2s',
    backgroundColor: '#fff',
  };

  const errorInputStyle = {
    ...inputStyle,
    borderColor: '#dc3545',
    backgroundColor: '#fff8f8'
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '6px',
    display: 'block'
  };

  const buttonStyle = {
    padding: '12px 20px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 'bold',
    marginTop: '10px',
    width: '100%',
    transition: 'background-color 0.2s'
  };
  
  const signupButtonStyle = {
    backgroundColor: 'transparent',
    color: primaryColor,
    border: `1px solid ${primaryColor}`,
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    marginTop: '15px',
    width: '100%',
    textAlign: 'center',
    textDecoration: 'none',
    transition: 'all 0.2s'
  };

  // 입력값 변경 핸들러
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [id]: value
    }));

    // 유효성 검사
    validateField(id, value);
  };

  // 필드별 유효성 검사
  const validateField = (id, value) => {
    let errors = {...localValidationErrors};
    
    if (id === 'user_id') {
      if (!value.trim()) {
        errors.user_id = t('ID를 입력해주세요');
      } else if (value.length < 2 || value.length > 20) {
        errors.user_id = t('ID는 2~20자로 입력해주세요');
      } else {
        delete errors.user_id;
      }
    }
    
    if (id === 'password') {
      if (!value) {
        errors.password = t('비밀번호를 입력해주세요');
      } else {
        delete errors.password;
      }
    }
    
    setLocalValidationErrors(errors);
  };

  // 폼 전체 유효성 검사
  const validateForm = () => {
    const errors = {};
    const { user_id, password } = formData;
    
    if (!user_id.trim()) {
      errors.user_id = t('ID를 입력해주세요');
    } else if (user_id.length < 2 || user_id.length > 20) {
      errors.user_id = t('ID는 2~20자로 입력해주세요');
    }
    
    if (!password) {
      errors.password = t('비밀번호를 입력해주세요');
    }
    
    setLocalValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 유효성 검사
    if (!validateForm()) {
      return;
    }
    
    // Redux 액션 디스패치
    dispatch(loginUser(formData));
  };

  // 회원가입 링크 핸들러
  const handleSignupClick = () => {
    window.location.href = '/signup';
  };

  return (
    <div style={{position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden'}}>
      {/* 배경 이미지/비디오 */}
      {backgroundType === 'image' && backgroundUrl && (
        <img src={backgroundUrl} alt="bg" style={{position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', zIndex: 0}} />
      )}
      {backgroundType === 'video' && backgroundUrl && (
        <>
          <video
            src={backgroundUrl}
            autoPlay
            loop
            playsInline
            muted
            style={{position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', zIndex: 0}}
          />
          {/* 검은색 알파 오버레이 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.69)',
              zIndex: 1
            }}
          />
        </>
      )}
      <div style={{position: 'relative', zIndex: 1, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <div className="login-form" style={defaultStyle}>
          {withcookieData?.skin?.company?.id !== 24 && (
            <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{t('로그인')}</h2>
          )}
          {withcookieData?.skin?.company?.id == 24 && (
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <img src={withcookieData?.skin?.androidAppIconImage} alt="logo" style={{maxWidth:'300px', objectFit: 'contain', margin:'0 auto'}} />
            </div>
          )}
          {loginSuccess ? (
            <div style={{ textAlign: 'center', color: 'green', marginBottom: '20px', padding: '15px', backgroundColor: '#f8fff8', borderRadius: '4px' }}>
              <p>{t('로그인 성공! 환영합니다.')}</p>
              <p>{t('잠시 후 홈페이지로 이동합니다.')}</p>
            </div>
          ) : (
            <form style={formStyle} onSubmit={handleSubmit}>
              <div style={formRowStyle}>
                <label htmlFor="user_id" style={labelStyle}>{t('아이디')}</label>
                <input 
                  id="user_id" 
                  type="text" 
                  placeholder={t('아이디 입력 (2~20자)')} 
                  required 
                  style={validationErrors.user_id ? errorInputStyle : inputStyle}
                  value={formData.user_id}
                  onChange={handleChange}
                  minLength="2"
                  maxLength="20"
                />
                {validationErrors.user_id && (
                  <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                    {validationErrors.user_id}
                  </div>
                )}
              </div>
              <div style={formRowStyle}>
                <label htmlFor="password" style={labelStyle}>{t('비밀번호')}</label>
                <input 
                  id="password" 
                  type="password" 
                  placeholder={t('비밀번호 입력')} 
                  required 
                  style={validationErrors.password ? errorInputStyle : inputStyle}
                  value={formData.password}
                  onChange={handleChange}
                />
                {validationErrors.password && (
                  <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                    {validationErrors.password}
                  </div>
                )}
              </div>
              {loginError && (
                <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '15px', padding: '12px', backgroundColor: '#fff8f8', borderRadius: '4px', borderLeft: '4px solid #dc3545' }}>
                  {typeof loginError === 'string' ? t(loginError) : loginError}
                </div>
              )}
              <button 
                type="submit" 
                style={{
                  ...buttonStyle,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  backgroundColor: loading ? secondaryColor : primaryColor,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
                disabled={loading}
              >
                {loading ? t('로그인 중...') : t('로그인')}
              </button>
              <a 
                href="/signup" 
                style={signupButtonStyle}
                onClick={(e) => {
                  e.preventDefault();
                  handleSignupClick();
                }}
              >
                {t('회원가입')}
              </a>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;